import { emitExternalToast } from "../components/toast";

export type QueryValue = string | number | boolean | undefined | null;
export type QueryParams = { readonly [k: string]: QueryValue };

export interface FetchOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: unknown;
  query?: QueryParams;
  headers?: Record<string, string>;
  /** Suppress automatic toast on failure (caller handles error). */
  silent?: boolean;
}

export type FetchResult<T> =
  | { ok: true; data: T; status: number }
  | { ok: false; error: string; status: number; details?: unknown };

let getAccessToken: () => string | undefined = () => undefined;

/** Wired from App.tsx so non-React modules can grab the current Keycloak
 * access_token without importing react-oidc-context. */
export function registerAuthForFetch(getter: () => string | undefined): void {
  getAccessToken = getter;
}

function buildUrl(path: string, query?: FetchOptions["query"]): string {
  if (!query) return path;
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    usp.append(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function mtFetch<T = unknown>(
  path: string,
  options: FetchOptions = {},
): Promise<FetchResult<T>> {
  const { method = "GET", body, query, headers = {}, silent = false } = options;

  const token = getAccessToken();
  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...headers,
  };
  if (token) finalHeaders["Authorization"] = `Bearer ${token}`;
  if (body !== undefined && !finalHeaders["Content-Type"]) {
    finalHeaders["Content-Type"] = "application/json";
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers: finalHeaders,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: "include",
    });
  } catch (networkError) {
    const message =
      networkError instanceof Error ? networkError.message : "network error";
    if (!silent) emitExternalToast(`Сеть: ${message}`, "error");
    return { ok: false, error: message, status: 0 };
  }

  if (response.status === 204) {
    return { ok: true, data: undefined as unknown as T, status: 204 };
  }

  let parsed: unknown;
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    parsed = await response.json().catch(() => null);
  } else {
    parsed = await response.text().catch(() => "");
  }

  if (!response.ok) {
    const errorMsg =
      (parsed as { detail?: string } | null)?.detail ??
      `HTTP ${response.status}`;
    if (!silent) emitExternalToast(errorMsg, "error");
    return {
      ok: false,
      status: response.status,
      error: errorMsg,
      details: parsed,
    };
  }

  return { ok: true, data: parsed as T, status: response.status };
}
